import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Table, Button, Card, Input, Select, Space, Popconfirm, Spin, Tooltip, Tag, message } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SearchOutlined,
  CopyOutlined,
  EnvironmentOutlined,
  BankOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../stores/root-store-context';
import { useNavigate } from 'react-router';
import CrmUserStamp from '../../components/crm/CrmUserStamp';
import './crm.css';

// Universal one-line address: country, city/district, street line (legacy rows kept the street in locationName)
const formatAddress = (a: any): string =>
  a ? [a.country, a.city, a.addressLine1 || a.locationName].filter((x: any) => x && String(x).trim()).join(', ') : '';

const ExternalCompanyManagement: React.FC = observer(() => {
  const { t } = useTranslation();
  const { applicationStore, crmStore } = useStore();
  const navigate = useNavigate();

  const [search, setSearch] = useState<{ [k: string]: any }>({});
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Сбрасываем пагинацию на первую страницу при изменении фильтров
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  useEffect(() => {
    crmStore.getExternalCompanies();
    crmStore.getCategories();
  }, [crmStore]);

  const data = crmStore.externalCompanies
    .filter(c => {
      const { name, edrpou, categoryIds } = search;
      if (name && !c.name.toLowerCase().includes(name.toLowerCase())) return false;
      if (edrpou && !c.edrpou?.includes(edrpou)) return false;
      if (categoryIds && categoryIds.length > 0) {
        if (!c.categoryId || !categoryIds.includes(c.categoryId)) return false;
      }
      return true;
    })
    .sort((a, b) => b.id - a.id);

  const rangeStart = data.length > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const rangeEnd = Math.min(currentPage * pageSize, data.length);

  const columns: any[] = [
    { title: '#', dataIndex: 'id', key: 'id', width: '5%', sorter: (a: any, b: any) => a.id - b.id, defaultSortOrder: 'descend' },
    {
      title: t('Name'),
      dataIndex: 'name',
      key: 'name',
      width: '22%',
      sorter: (a: any, b: any) => (a.name || '').localeCompare(b.name || ''),
      render: (name: string) => <span className="crm-contact-name">{name}</span>,
    },
    {
      title: t('EDRPOU'),
      dataIndex: 'edrpou',
      key: 'edrpou',
      width: '14%',
      sorter: (a: any, b: any) => (a.edrpou || '').localeCompare(b.edrpou || ''),
      render: (edrpou: string) => {
        if (!edrpou) return <span style={{ color: '#cbd5e1' }}>—</span>;
        return (
          <div className="crm-interactive-cell" onClick={(e) => e.stopPropagation()}>
            <span className="crm-edrpou-badge">{edrpou}</span>
            <Tooltip title={t('crm.copyEdrpou', 'Скопіювати ЄДРПОУ')}>
              <button
                type="button"
                className="crm-copy-btn"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigator.clipboard.writeText(edrpou);
                  message.success(`${t('EDRPOU')}: ${t('crm.copied', 'Скопійовано в буфер!')}`);
                }}
              >
                <CopyOutlined />
              </button>
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: t('Category'),
      key: 'category',
      width: '14%',
      sorter: (a: any, b: any) => {
        const catA = (a.categoryId ? crmStore.categoriesMap.get(a.categoryId)?.name : null) || a.category?.name || '';
        const catB = (b.categoryId ? crmStore.categoriesMap.get(b.categoryId)?.name : null) || b.category?.name || '';
        return catA.localeCompare(catB);
      },
      render: (_: unknown, r: any) => {
        const name = (r.categoryId ? crmStore.categoriesMap.get(r.categoryId)?.name : null) || r.category?.name;
        if (!name) return <span style={{ color: '#cbd5e1' }}>—</span>;
        return <Tag color="blue" className="crm-cat-tag">{name}</Tag>;
      },
    },
    {
      title: t('Address'),
      dataIndex: 'addresses',
      key: 'addresses',
      width: '16%',
      sorter: (a: any, b: any) => formatAddress(a.addresses?.[0]).localeCompare(formatAddress(b.addresses?.[0])),
      render: (arr: any[]) => {
        const addr = formatAddress(arr?.[0]);
        if (!addr) return <span style={{ color: '#cbd5e1' }}>—</span>;
        return (
          <span className="crm-address-cell" title={addr}>
            <EnvironmentOutlined style={{ color: '#ef4444', marginRight: 4, flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{addr}</span>
          </span>
        );
      },
    },
    {
      title: t('CreatedBy'),
      key: 'createdBy',
      width: '11%',
      sorter: (a: any, b: any) => (a.createdAt || '').localeCompare(b.createdAt || ''),
      render: (_: unknown, r: any) => <CrmUserStamp name={r.createdByName} date={r.createdAt} />,
    },
    {
      title: t('UpdatedBy'),
      key: 'updatedBy',
      width: '11%',
      sorter: (a: any, b: any) => (a.updatedAt || '').localeCompare(b.updatedAt || ''),
      render: (_: unknown, r: any) => <CrmUserStamp name={r.updatedByName} date={r.updatedAt} />,
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: '12%',
      render: (_: unknown, r: any) => (
        <Space size="small" onClick={e => e.stopPropagation()}>
          <Button
            className="crm-action-btn crm-action-btn-edit"
            icon={<EditOutlined />}
            onClick={() =>
              navigate(`/${applicationStore.company?.Tenant}/crm/company/${r.id}?edit=1`)
            }
          >
            {t('Edit')}
          </Button>
          <Popconfirm
            title={t('DeleteCompanyConfirm')}
            onConfirm={() => crmStore.deleteExternalCompany(r.id)}
          >
            <Button
              className="crm-action-btn crm-action-btn-delete"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const hasFilters = Boolean(search.name || search.edrpou || (search.categoryIds && search.categoryIds.length > 0));

  return (
    <Spin spinning={crmStore.isLoading}>
      <Card bordered={false}>
        <div className="crm-filter-toolbar">
          <div className="crm-filter-inputs">
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
              placeholder={t('Name')}
              value={search.name}
              onChange={e => setSearch({ ...search, name: e.target.value })}
              style={{ width: 200 }}
            />
            <Input
              allowClear
              placeholder={t('EDRPOU')}
              value={search.edrpou}
              onChange={e => setSearch({ ...search, edrpou: e.target.value })}
              style={{ width: 160 }}
            />
            <Select
              mode="multiple"
              maxTagCount="responsive"
              allowClear
              placeholder={t('Category')}
              style={{ minWidth: 220, maxWidth: 320 }}
              value={search.categoryIds || []}
              onChange={val => setSearch({ ...search, categoryIds: val })}
            >
              {crmStore.categories.map(cat => (
                <Select.Option key={cat.id} value={cat.id}>
                  {cat.name}
                </Select.Option>
              ))}
            </Select>
            <Button
              className="crm-reset-btn"
              icon={<ReloadOutlined />}
              onClick={() => setSearch({})}
              disabled={!hasFilters}
            >
              {t('Reset')}
            </Button>
          </div>

          <div className="crm-filter-actions">
            <div className="crm-gmail-counter">
              <span>
                {rangeStart}–{rangeEnd} {t('crm.of', 'із')} {data.length}
              </span>
              {data.length !== crmStore.externalCompanies.length && (
                <span className="crm-gmail-counter-secondary">
                  ({t('crm.total', 'всього')} {crmStore.externalCompanies.length})
                </span>
              )}
            </div>

            <Button
              type="primary"
              className="crm-primary-btn"
              icon={<PlusOutlined />}
              onClick={() =>
                navigate(`/${applicationStore.company?.Tenant}/crm/company/new`)
              }
            >
              {t('AddCompany')}
            </Button>
          </div>
        </div>

        <Table
          className="crm-table"
          dataSource={data}
          columns={columns}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          rowClassName={() => 'crm-row-clickable'}
          onRow={(r: any) => ({
            onClick: () => navigate(`/${applicationStore.company?.Tenant}/crm/company/${r.id}`),
          })}
          pagination={{
            current: currentPage,
            pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
            onChange: (page, size) => {
              setCurrentPage(page);
              if (size && size !== pageSize) {
                setPageSize(size);
              }
            },
          }}
        />
      </Card>
    </Spin>
  );
});

export default ExternalCompanyManagement;
